import {
  ALLOWED_ARRAY_INNER_TYPES,
  AllowedArrayInnerTypes,
  MAX_NUMBER_DECIMAL_PLACES,
  MIN_NUMBER_DECIMAL_PLACES,
  checkValidName,
  enumOptionInputListSchema,
  getNextOrder,
} from '@doss/common-all'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-premium'
import { first } from 'lodash'
import { createContext, useContext, useMemo, useState } from 'react'
import * as Yup from 'yup'
import {
  DataType,
  FormatPreset,
  IndexType,
  useCreateTableColumnMutation,
  useGetTablesQuery,
  useUpdateTableColumnMutation,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import {
  addColumnToTable,
  addColumnToTableTab,
  prepareCreateColumnData,
  prepareUpdateColumnData,
} from '~/common/utils/table-column'
import { useDossSnackbar } from '../shared/DossSnackbar'
import { toEnumOptionInputs } from '../table-columns/enum-option-form-values'
import { ColumnFormValues, EnumOptionFormValue } from '../table-columns/types'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'
import { OrganizationTable } from './types'

export interface TableColumnFormTableColumn {
  __typename: 'TableColumn'
  id: string
  name: string
  description: string | null
  order: number
  type: DataType
  innerType: DataType | null
  isNullable: boolean
  isReadOnly: boolean
  indexType: IndexType
  indexPrefix: string
  indexSuffix: string
  indexAutogenerate: boolean
  enumOptions: string[] | null
  displayImagesInline: boolean
  hasNonNullCells?: boolean
  hasNullCells?: boolean
  formula: { id: string } | null
  lookupColumnId?: string | null
  lookupColumnGroupId?: string | null
  lookupTableId?: string | null
}

interface TableColumnFormContextValue {
  formulaError: string | null
  setFormulaError: (error: string | null) => void
  handleCreate: (values: ColumnFormValues) => Promise<void>
  handleUpdate: (values: ColumnFormValues) => Promise<void>
  getValidationSchema: (
    defaultValueIsRequired: boolean,
    columns: TableColumnFormTableColumn[],
    columnId?: string,
  ) => Yup.AnyObjectSchema
  nextAvailableOrder: number
  organizationTables: OrganizationTable[]
}

export function TableColumnFormProvider({
  children,
  table,
  selectedTabId,
  columnVisibilityModel,
  setColumnVisibilityModel,
}: {
  children: React.ReactNode
  table: {
    __typename: 'Table'
    id: string
    columns: Array<{
      id: string
      name: string
      order: number
    }>
  }
  selectedTabId?: string
  columnVisibilityModel?: GridColumnVisibilityModel
  setColumnVisibilityModel?: (visibilityModel: GridColumnVisibilityModel) => void
}) {
  const [formulaError, setFormulaError] = useState<string | null>(null)
  const snackbar = useDossSnackbar()

  const { t } = useTypedTranslation()
  const { data: tablesData } = useGetTablesQuery()

  const [createTableColumn] = useCreateTableColumnMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }
      const newColumn = data.createTableColumn

      // Add the new column to its table in the cache
      addColumnToTable(cache, table, newColumn)

      // If the column has a tab associated with it, add it to the tab in the cache
      // The column will have at most one table tab
      const firstTableTab = first(newColumn.tableTabs)
      if (firstTableTab) {
        // Add the column to the table tab's cache
        addColumnToTableTab(cache, newColumn, firstTableTab)

        if (columnVisibilityModel && setColumnVisibilityModel) {
          // Update the column visibility model to include the newly added column.
          // This prevents the "Save" button from appearing in the table toolbar if creating
          // a table column from the table view
          const updatedColumnVisibilityModel = { ...columnVisibilityModel }
          updatedColumnVisibilityModel[newColumn.id] = true
          setColumnVisibilityModel(updatedColumnVisibilityModel)
        }
      }
    },
  })

  const [updateTableColumn] = useUpdateTableColumnMutation()

  // Note: Order is now generated on the backend, but we still calculate it on the frontend
  // for validation purposes (e.g., checking if this will be the first column)
  const nextAvailableOrder = useMemo(() => {
    return getNextOrder(table.columns)
  }, [table.columns])

  const handleCreate = async (values: ColumnFormValues) => {
    snackbar.showLoading(
      t('table.create_column.creating'),
      undefined,
      undefined,
      undefined,
      <LoadingSpinner type="table-cell" />,
    )
    const columnData = prepareCreateColumnData(values, selectedTabId)

    await createTableColumn({
      variables: {
        input: { ...columnData, tableId: table.id },
      },
    })
      .then((result) => {
        if (!result.data) {
          snackbar.showError(t('table.create_column.create_error'))
          return
        }
        snackbar.showSuccess(t('table.create_column.create_success'), undefined, undefined, 1500)
      })
      .catch((err) => {
        snackbar.showError(err.message)
        throw err
      })
  }

  const handleUpdate = async (values: ColumnFormValues) => {
    snackbar.showLoading(
      t('table.create_column.updating'),
      undefined,
      undefined,
      undefined,
      <LoadingSpinner type="table-cell" />,
    )
    const columnData = prepareUpdateColumnData(values)

    await updateTableColumn({
      variables: {
        input: { ...columnData },
      },
    })
      .then((result) => {
        if (!result.data) {
          snackbar.showError(t('table.create_column.update_error'))
          return
        }
        snackbar.showSuccess(t('table.create_column.update_success'), undefined, undefined, 1500)
      })
      .catch((err) => {
        snackbar.showError(err.message)
        throw err
      })
  }

  const getValidationSchema = (
    defaultValueIsRequired: boolean,
    columns: TableColumnFormTableColumn[],
    columnId?: string,
  ) => {
    return Yup.object({
      name: Yup.string()
        .required(t('table.create_column.name_required'))
        .test(
          'unique-name',
          t('table.create_column.name_taken'),
          // Function to check for uniqueness
          (value) => {
            try {
              checkValidName(
                columns,
                value,
                columnId,
                t('table.create_column.name_exists', { name: value }),
              )
              return true
            } catch (err) {
              return false
            }
          },
        ),
      description: Yup.string().nullable(),
      tableId: Yup.string().optional(),

      type: Yup.mixed()
        .oneOf(Object.values(DataType))
        .required(t('table.create_column.type_required')),
      isNullable: Yup.boolean().required(),
      formatOptions: Yup.mixed().when(['type', 'formatPreset'], ([type, formatPreset], schema) => {
        const decimalPlaces = Yup.number()
          .integer()
          .min(MIN_NUMBER_DECIMAL_PLACES, t('table.create_column.format.decimal_places_max'))
          .max(MAX_NUMBER_DECIMAL_PLACES, t('table.create_column.format.decimal_places_max'))

        if (
          formatPreset === FormatPreset.NUMBER_PLAIN ||
          formatPreset === FormatPreset.NUMBER_DECIMAL ||
          formatPreset === FormatPreset.NUMBER_PERCENT
        ) {
          return Yup.object({ decimalPlaces: decimalPlaces.required() }).required()
        }

        if (formatPreset === FormatPreset.NUMBER_UNIT) {
          return Yup.object({
            decimalPlaces: decimalPlaces.required(),
            unit: Yup.string().required(t('table.create_column.format.unit_required')),
          }).required()
        }

        if (
          (type === DataType.DATE ||
            type === DataType.DATE_TIME ||
            type === DataType.FORMULA_DATE ||
            type === DataType.FORMULA_DATE_TIME) &&
          formatPreset === FormatPreset.DATE_CUSTOM
        ) {
          return Yup.object({
            pattern: Yup.string().defined(t('table.create_column.format.custom_date_required')),
          }).required()
        }

        return schema.nullable()
      }),
      formulaString: Yup.string().nullable(), // Nullable field
      formulaId: Yup.string().nullable(), // Nullable field
      shouldCreateNewLookupGroup: Yup.boolean().required(), // Required field
      id: Yup.string().optional(), // Optional field for RequireExactlyOne
      tempId: Yup.string().optional(), // Optional field for RequireExactlyOne
      innerType: Yup.mixed()
        .oneOf(ALLOWED_ARRAY_INNER_TYPES)
        .when('type', {
          is: DataType.ARRAY,
          then: (schema) => schema.required(t('table.create_column.inner_type_required')),
          otherwise: (schema) =>
            schema.nullable().oneOf([null], t('table.create_column.inner_type_null')),
        }),
      enumOptions: Yup.array()
        .of(Yup.string())
        // Validate based on both 'type' and 'innerType' being ENUM
        .when(['type', 'innerType'], {
          is: (type: DataType, innerType: AllowedArrayInnerTypes) =>
            type === DataType.ENUM || innerType === DataType.ENUM,
          then: (schema) =>
            schema
              .min(1, t('table.create_column.enum_options_required'))
              .required(t('table.create_column.enum_options_required')),
          otherwise: (schema) =>
            schema
              .notRequired()
              .nullable()
              .test({
                name: 'isEmpty',
                message: 'Options must be empty',
                test: (value) => value === null || value?.length === 0,
              }),
        }),
      enumOptionInputs: Yup.array().when(['type', 'innerType'], {
        is: (type: DataType, innerType: AllowedArrayInnerTypes) =>
          type === DataType.ENUM || innerType === DataType.ENUM,
        then: (schema) =>
          schema
            .min(1, t('table.create_column.enum_options_required'))
            .required(t('table.create_column.enum_options_required'))
            // Defer to the shared schema so the form can't drift from what the write path enforces
            .test({
              name: 'validOptionInputs',
              test: (value, ctx) => {
                const result = enumOptionInputListSchema.safeParse(
                  toEnumOptionInputs(value as EnumOptionFormValue[]),
                )
                return (
                  result.success || ctx.createError({ message: result.error.issues[0]?.message })
                )
              },
            }),
        otherwise: (schema) =>
          schema
            .notRequired()
            .nullable()
            .test({
              name: 'isEmpty',
              message: 'Options must be empty',
              test: (value) => value === null || value?.length === 0,
            }),
      }),
      indexType: Yup.mixed().when('type', {
        is: DataType.ID,
        then: (schema) => schema.oneOf([IndexType.NUMBER, IndexType.STRING]),
        otherwise: (schema) => schema.oneOf([IndexType.NONE]),
      }),
      indexPrefix: Yup.string().when('type', {
        is: DataType.ID,
        otherwise: (schema) => schema.matches(/^$/, t('table.create_column.index_prefix_required')),
      }),
      indexSuffix: Yup.string().when('type', {
        is: DataType.ID,
        otherwise: (schema) => schema.matches(/^$/, t('table.create_column.index_suffix_required')),
      }),
      indexAutogenerate: Yup.boolean()
        .required()
        .when('type', {
          is: DataType.ID,
          otherwise: (schema) =>
            schema.isFalse(t('table.create_column.index_autogenerate_required')),
        }),
      displayImagesInline: Yup.boolean()
        .required()
        // Validate based on both 'type' and 'innerType' being FILE_URL
        .when(['type', 'innerType'], {
          is: (type: DataType, innerType: AllowedArrayInnerTypes) =>
            type === DataType.FILE_URL || innerType === DataType.FILE_URL,
          otherwise: (schema) =>
            schema.isFalse(t('table.create_column.display_images_inline_required')),
        }),
      defaultValue: Yup.mixed().when([], () => {
        return defaultValueIsRequired
          ? Yup.mixed()
              .required(t('table.create_column.default_value_required'))
              .test(
                'is-not-empty-array',
                t('table.create_column.default_value_required'),
                (value) => {
                  // Check if it's an empty array (which should be invalid when required)
                  if (
                    typeof value === 'object' &&
                    'type' in value &&
                    value.type === 'ARRAY' &&
                    'data' in value &&
                    Array.isArray(value.data) &&
                    value.data.length === 0
                  ) {
                    return false
                  }
                  return true
                },
              )
          : Yup.mixed().nullable()
      }),
      lookupColumnGroupId: Yup.string().nullable(),
      lookupTableId: Yup.string().nullable(),
      lookupColumnId: Yup.string()
        .nullable()
        .when(['type', 'innerType'], {
          is: (type: DataType, innerType: AllowedArrayInnerTypes) =>
            type === DataType.LOOKUP || innerType === DataType.LOOKUP,
          then: (schema) =>
            schema.required(t('table.create_column.lookup_column_group_id_required')),
          otherwise: (schema) =>
            schema
              .nullable()
              .oneOf([null], t('table.create_column.non_lookup_type_column_lookup_field')),
        }),
    }).test('lookup-id-required', 'Please select a field for the lookup', function (value) {
      const { type, innerType, lookupColumnGroupId, lookupTableId } = value
      if (type === DataType.LOOKUP || innerType === DataType.LOOKUP) {
        // For lookup type, have to provide a lookup column group id or a lookup table id
        if (!lookupColumnGroupId && !lookupTableId) {
          return this.createError({
            path: 'lookupColumnGroupId',
            message: t('table.create_column.lookup_column_group_id_required'),
          })
        }

        // For lookup type, cannot provide both a lookup column group id and a lookup table id
        if (lookupColumnGroupId && lookupTableId) {
          return this.createError({
            path: 'lookupColumnGroupId',
            message: t('table.create_column.lookup_column_group_id_required'),
          })
        }
      }
      return true
    })
  }

  return (
    <TableColumnFormContext.Provider
      value={{
        formulaError,
        setFormulaError,
        handleCreate,
        handleUpdate,
        getValidationSchema,
        nextAvailableOrder,
        organizationTables: tablesData?.tables ?? [],
      }}
    >
      {children}
    </TableColumnFormContext.Provider>
  )
}

export const useTableColumnForm = () => {
  const context = useContext(TableColumnFormContext)

  if (!context) {
    throw new Error('useTableColumnForm must be used within a TableColumnFormProvider')
  }

  return context
}

const TableColumnFormContext = createContext<TableColumnFormContextValue | null>(null)
